import React, { useCallback, useEffect, useRef, useState } from 'react';

import { SERVICE_CATEGORIES, SERVICES as MARKETPLACE_SERVICES } from 'ux/Tenant/Services/constants';

import './Services.scss';

const categoryImageUrl = (slug: string) => SERVICE_CATEGORIES.find(c => c.slug === slug)?.imageUrl ?? '';

const marketplaceServiceImageUrl = (id: string) => MARKETPLACE_SERVICES.find(s => s.id === id)?.imageUrl ?? '';

/** Card art aligns with tenant Services marketplace (categories + specific listings). */
const SERVICES = [
  {
    emoji: '🧹',
    label: 'Cleaning',
    desc: "Regular and deep cleans scheduled automatically around your team's calendar — no coordination needed.",
    link: 'https://www.codi.com/cleaning/',
    cardImageUrl: categoryImageUrl('cleaning')
  },
  {
    emoji: '🍎',
    label: 'Pantry restocking',
    desc: "Snacks, drinks, and supplies restocked automatically — based on your team's preferences and usage.",
    link: 'https://www.codi.com/restocking/',
    cardImageUrl: marketplaceServiceImageUrl('pantry-inventory-tracking-auto-ordering')
  },
  {
    emoji: '🍱',
    label: 'Catering',
    desc: "From team lunches to board meetings, Codi sources and coordinates catering from local vendors you'll love.",
    link: null,
    cardImageUrl: marketplaceServiceImageUrl('ad-hoc-catering')
  },
  {
    emoji: '🌿',
    label: 'Plant service',
    desc: 'Biophilic office design maintained for you — plants sourced, installed, and kept alive without any effort on your end.',
    link: null,
    cardImageUrl: marketplaceServiceImageUrl('plant-care')
  },
  {
    emoji: '💻',
    label: 'IT services',
    desc: 'From equipment setup to troubleshooting, Codi dispatches trusted IT consultants to your office on demand.',
    link: null,
    cardImageUrl: categoryImageUrl('it')
  },
  {
    emoji: '🪑',
    label: 'Furniture & equipment',
    desc: 'Need desks, chairs, or AV gear? Codi handles sourcing, delivery, and installation end to end.',
    link: null,
    cardImageUrl: marketplaceServiceImageUrl('furniture-assembly-repairs')
  },
  {
    emoji: '🎨',
    label: 'Design',
    desc: 'Office refreshes, signage, and branded spaces — coordinated with vetted design and fit-out partners.',
    link: null,
    cardImageUrl: categoryImageUrl('design')
  },
  {
    emoji: '🔧',
    label: 'Maintenance',
    desc: 'Leaks, broken fixtures, HVAC issues — Codi triages every request and dispatches the right vendor fast.',
    link: null,
    cardImageUrl: categoryImageUrl('maintenance')
  },
  {
    emoji: '💬',
    label: 'Building communication',
    desc: 'Codi liaises with your property manager and building team so you never have to chase an email again.',
    link: null,
    cardImageUrl: marketplaceServiceImageUrl('visitor-management')
  }
];

const INTERVAL = 6000;

export default function Services() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const goTo = useCallback((i: number) => {
    setActive(i);
    setAnimKey(k => k + 1);
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive(cur => {
        const next = (cur + 1) % SERVICES.length;
        setAnimKey(k => k + 1);
        return next;
      });
    }, INTERVAL);
  }, []);

  useEffect(() => {
    if (!visible) return;
    startTimer();
    return () => {
      clearInterval(timerRef.current);
    };
  }, [visible, startTimer]);

  const handlePillClick = (i: number) => {
    goTo(i);
    startTimer();
  };

  const svc = SERVICES[active];

  return (
    <section className="services" ref={sectionRef}>
      <div className="services__container">
        {/* ── Copy ── */}
        <div className={`services__copy ${visible ? 'services__copy--visible' : ''}`}>
          <h2 className="services__heading">
            One portal to
            <br />
            rule them all.
          </h2>
          <p className="services__sub">
            From our trusted local network to your own favorite vendors, Codi manages every service your office needs —
            without you lifting a finger.
          </p>
        </div>

        {/* ── Two-column layout ── */}
        <div className={`services__layout ${visible ? 'services__layout--visible' : ''}`}>
          {/* Left: pill cloud */}
          <div className="services__pills">
            {SERVICES.map((s, i) => (
              <button
                key={i}
                className={`services__pill ${active === i ? 'services__pill--active' : ''}`}
                onClick={() => handlePillClick(i)}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <span className="services__pill-emoji">{s.emoji}</span>
                {s.label}
              </button>
            ))}
          </div>

          {/* Right: detail card */}
          <div className="services__card-wrap">
            <div className="services__card" key={animKey}>
              {svc.cardImageUrl ? (
                <div className="services__card-media">
                  <img className="services__card-image" src={svc.cardImageUrl} alt="" />
                </div>
              ) : (
                <div className="services__card-media services__card-media--emoji" aria-hidden>
                  <span className="services__card-emoji">{svc.emoji}</span>
                </div>
              )}
              <div className="services__card-body">
                <div className="services__card-label">{svc.label}</div>
                <p className="services__card-desc">{svc.desc}</p>
                {svc.link && (
                  <a href={svc.link} className="services__card-link" target="_blank" rel="noopener noreferrer">
                    Learn more
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6h7M6.5 3.5L9 6l-2.5 2.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                )}
              </div>
              {/* Progress bar */}
              <div className="services__card-progress">
                <div className="services__card-progress-bar" key={`bar-${animKey}`} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
